import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Collapse } from 'reactstrap';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';

import { currencyCode } from 'Helpers/Geo/data';
import SpecialFormattedNumber from 'Containers/Utility/Price/SpecialFormattedNumber';
import Terms from 'Components/Vouchers/Terms';
import VoucherBadge from 'Components/Vouchers/VoucherBadge';
import useExtensionInstalled from 'Components/Hooks/useExtensionInstalled';

const Vouchers = ({
  voucher,
  appliedVoucher,
  accountVoucher,
}) => {
  const { assistantInstalled } = useExtensionInstalled();
  const [collapse, setCollapse] = useState(false);

  const voucherId = voucher?._id || '';
  const appliedVoucherId = appliedVoucher?.voucher?._id || '';

  let voucherCurrentlyApplied = false;
  if (voucherId === appliedVoucherId) {
    voucherCurrentlyApplied = true;
  }

  const voucherLink = voucher ? `add_voucher=${voucher.code}` : '';

  let singleProductVoucher = false;
  if (!isEmpty(voucher.required_products) && voucher.required_products.length === 1) {
    singleProductVoucher = true;
  }

  const renderButton = () => {
    if (voucher.assistant_exclusive && !assistantInstalled) {
      return (
        <div className="voucher-message-warning">
          <FormattedMessage id="voucher-box.extension-installed" defaultMessage="You must have the Fanatical Browser Extension installed to use this code." />
          &nbsp;
          <Link to={'/browser-extension'}>
            <FormattedMessage id="voucher-box.find-out-more" defaultMessage="Find out more here" />
          </Link>.
        </div>
      );
    }

    if (voucherCurrentlyApplied) {
      return (
        <div className="voucher-message">
          <FormattedMessage id="voucher-box.currently-applied" defaultMessage="This coupon is currently applied to " />
          <Link to={'/cart'}>
            <FormattedMessage id="voucher-box.your-cart" defaultMessage="your cart" />
          </Link>.
        </div>
      );
    }

    if (voucher.add_to_basket) {
      return (
        <a
          href={`/cart?${voucherLink}&add_to_basket=${voucher.add_to_basket}`}
        >
          <Button outline color="primary">
            <FormattedMessage id="voucher-box.add-with-voucher" defaultMessage="Add to Cart with coupon" />
          </Button>
        </a>
      );
    }

    return (
      <a href={`/cart?${voucherLink}`}>
        <Button outline color="primary">
          <FormattedMessage id="voucher-box.add-apply-voucher" defaultMessage="Apply coupon" />
        </Button>
      </a>
    );
  };

  return (
    <article className={classNames('voucher-box', { 'account-voucher': accountVoucher })}>
      <VoucherBadge newsletter={voucher.newsletter} />
      <div className="voucher-name">
        {!singleProductVoucher &&
        <FormattedMessage
          id="voucher.name"
          defaultMessage="{title}"
          values={{ title: voucher.title }}
        />
          }
        {singleProductVoucher &&
        <React.Fragment>
          {voucher.type_of_discount === 'money' &&
          <FormattedMessage
            id="voucher.name.single-product.money"
            defaultMessage="Save an extra {money} on {name}"
            values={{
              money: <SpecialFormattedNumber
                value={voucher.amount[`${currencyCode}`]}
                style="currency"
              />,
              name: voucher.required_products[0].name,
            }}
          />
              }
          {voucher.type_of_discount === 'percent' &&
          <FormattedMessage
            id="voucher.name.single-product.percent"
            defaultMessage="Save an extra {discount}% on {name}"
            values={{
              discount: voucher.percent,
              name: voucher.required_products[0].name,
            }}
          />
              }
        </React.Fragment>
          }
        {voucher.newsletter &&
        <FormattedMessage id="voucher.subscribe" defaultMessage=" when you subscribe to our email newsletter" />
          }
      </div>
      <div className="voucher-body">
        <div className="voucher-code">
          <FormattedMessage
            id="voucher.description"
            defaultMessage="{description}"
            values={{ description: voucher.description }}
          />
        </div>
        <div className="voucher-code">
          <FormattedMessage id="voucher-box.code" defaultMessage="Code: " />
          <span>{voucher.code}</span>
        </div>

        <div className="button-container">
          <div className="voucher-button">
            {renderButton()}
          </div>
          <div className="voucher-terms-btn">
            <Button color="link" size="sm" onClick={() => setCollapse(prevState => !prevState)}>
              <FormattedMessage id="voucher-box.terms-conditions" defaultMessage="Terms and Conditions" />
            </Button>
          </div>
        </div>
      </div>

      {voucher.assistant_exclusive &&
      <div className="voucher-label">
        <div className="label-content">
          <FormattedMessage id="voucher-box.fanatical-extension-exclusive" defaultMessage="Fanatical Extension Exclusive" />
        </div>
        <div className="label-triangle" />
      </div>
        }

      <Collapse isOpen={collapse}>
        <div className="voucher-terms">
          <h6><FormattedMessage id="voucher-box.terms-coupon" defaultMessage="Coupon Terms and Conditions" /></h6>
          <Terms voucher={voucher} />
        </div>
      </Collapse>
    </article>
  );
};

Vouchers.propTypes = {
  voucher: PropTypes.shape({}),
  appliedVoucher: PropTypes.shape({}),
  accountVoucher: PropTypes.bool,
};

Vouchers.defaultProps = {
  voucher: {},
  appliedVoucher: {},
  accountVoucher: false,
};

export default Vouchers;
